// The boards and the redirect that decides which one somebody lands on.
//
// The split is GRO-58's whole point: leadership and viewers are asking "how is outbound going",
// strategists are asking "how do I outbound this account", and one table was serving neither
// well. Both routes are reachable by everybody -- the grant is the same and every account is in
// the payload either way -- so `/` chooses a landing rather than a permission.

import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ActivityView } from './routes/ActivityView';
import { AeView } from './routes/AeView';
import { AllAccountsView } from './routes/AllAccountsView';
import { AmplemarketCallbackView } from './routes/AmplemarketCallbackView';
import { AdminView } from './routes/AdminView';
import { BoardRoute } from './routes/BoardRoute';
import { ExtensionEmbedView } from './routes/ExtensionEmbedView';
import { Landing } from './routes/Landing';
import { QueueView } from './routes/QueueView';
import { RetireView } from './routes/RetireView';
import { ScoutView } from './routes/ScoutView';
import { SendsView } from './routes/SendsView';
import { SlackLinkView } from './routes/SlackLinkView';
import { SprintsView } from './routes/SprintsView';
import { StyleView } from './routes/StyleView';

// `/available` → `/all-accounts`, keeping the query and the hash.
function AvailableRedirect() {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: '/all-accounts', search, hash }} replace />;
}

// `/accounts` (the old oversight page) → the board, keeping `?account=` so old links still open the panel.
function AccountsRedirect() {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: '/execution', search, hash }} replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/activity" element={<ActivityView />} />
      {/* The board (go-live): a strategist's My Accounts, on their own accounts; Strategist Accounts,
          every strategist's, for admin, leadership and viewers. An AE is sent to their own book on `/ae`. */}
      <Route path="/execution" element={<BoardRoute />} />
      {/* An AE's My Accounts (GRO-360), their landing page. The server filters on the signed-in caller's email;
          an admin or leader sees it by viewing as an AE. */}
      <Route path="/ae" element={<AeView />} />
      {/* The old oversight page, folded into the board (go-live). */}
      <Route path="/accounts" element={<AccountsRedirect />} />
      {/* Every account in the book, for every role (go-live). It replaced Available. */}
      <Route path="/all-accounts" element={<AllAccountsView />} />
      {/* The weekly sprint check-in: a sprint's accounts and totals, for every role (GRO-515). */}
      <Route path="/sprints" element={<SprintsView />} />
      {/* Old Available links (Slack request cards, `/admin/queue`, bookmarks) keep their query
          and hash, so `#proposals` still lands on the queue. */}
      <Route path="/available" element={<AvailableRedirect />} />
      <Route path="/scout" element={<ScoutView />} />
      {/* The admin console. Reachable by URL for everybody and answered for nobody but an
          admin: `require_admin_view` decides, and the page redirects a caller it is not
          offered to rather than rendering a shell of refusals. */}
      {/* Kept resolving: the sheet now lives on `/all-accounts`. QueueView redirects. */}
      <Route path="/admin/queue" element={<QueueView />} />
      {/* The auto lane's review queue (W9). Admin and leadership; the page redirects anyone else. */}
      <Route path="/admin/sends" element={<SendsView />} />
      {/* The week's cold accounts, proposed for retirement. Admin only; the page redirects anyone else. */}
      <Route path="/admin/retire" element={<RetireView />} />
      <Route path="/admin" element={<AdminView />} />
      {/* Where Scout's private Slack sign-in link lands. Any signed-in person may open it;
          the server decides whether their role may be linked (GRO-161). */}
      <Route path="/slack/link" element={<SlackLinkView />} />
      {/* Where Amplemarket's OAuth consent returns (GRO-287). The page posts the code with the
          signed-in session; the server checks it is the same person who started the connect. */}
      <Route path="/amplemarket/callback" element={<AmplemarketCallbackView />} />
      {/* Settings → My style (W7): the strategist's own email voice. Offered with the Scout tab. */}
      <Route path="/settings/style" element={<StyleView />} />
      {/* What the Chrome extension's side panel frames (W8). No page chrome, no view-as. */}
      <Route path="/embed/extension" element={<ExtensionEmbedView />} />
      {/* Anything else is a mistyped URL. Send it through the landing redirect rather than to
          a fixed board, so a stale bookmark still lands somebody where they belong. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
